import React, { useEffect, useState, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import { centerCellStyle } from "../../styles/theme";

const ExcelViewer = ({ filePath, maxHeight = 500 }) => {
  const [rowData, setRowData] = useState([]);
  const [columnDefs, setColumnDefs] = useState([]);

  useEffect(() => {
    const fetchFile = async () => {
      if (!filePath) {
        setRowData([]);
        setColumnDefs([]);
        return;
      }

      setRowData([]);
      setColumnDefs([]);

      const response = await fetch(filePath);
      const blob = await response.blob();
      const fileName = filePath.split("/").pop().toLowerCase();

      // ----------------------------------------------------
      // CASE 1: Excel formats (.xlsx, .xls, .txt)
      // ----------------------------------------------------
      if (
        fileName.endsWith(".xlsx") ||
        fileName.endsWith(".xls") ||
        fileName.endsWith(".txt")
      ) {
        const arrayBuffer = await blob.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array", cellDates: true });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: false });

        if (!jsonData?.length) return;

        const maxCols = Math.max(...jsonData.map((r) => r.length));
        let headerRowIndex = 0;

        if (jsonData[0].length < maxCols && jsonData.length > 1) {
          headerRowIndex = 1;
        }

        const headerRow = jsonData[headerRowIndex];

        const headers = Array.from({ length: maxCols }, (_, i) => ({
          headerName: headerRow[i] || `Column ${i + 1}`,
          field: `col${i}`,
          sortable: true,
          filter: true,
          resizable: true,
        }));

        setColumnDefs(headers);

        const rows = jsonData.slice(headerRowIndex + 1).map((row) => {
          const obj = {};
          for (let i = 0; i < maxCols; i++) {
            obj[`col${i}`] = row[i] ?? "";
          }
          return obj;
        });

        setRowData(rows);
        return;
      }

      // ----------------------------------------------------
      // CASE 2: TSV (Nextclade) — strict tab parsing
      // ----------------------------------------------------
      if (fileName.endsWith(".tsv")) {
        const text = await blob.text();
        const lines = text.split(/\r?\n/).filter(Boolean);

        if (!lines.length) return;

        const headers = lines[0].split("\t");
        const rows = lines.slice(1).map((line) => {
          const cols = line.split("\t");
          const obj = {};
          headers.forEach((h, i) => {
            obj[h] = cols[i] ?? "";
          });
          return obj;
        });

        setColumnDefs(
          headers.map((h) => ({
            headerName: h,
            field: h,
            sortable: true,
            filter: true,
            resizable: true,
          }))
        );

        setRowData(rows);
        return;
      }

      // ----------------------------------------------------
      // CASE 3: CSV or other text formats
      // ----------------------------------------------------
      const text = await blob.text();

      Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        delimiter: "", // auto-detect for CSV
        complete: (results) => {
          if (!results.data?.length) return;

          const data = results.data;
          const keys = Object.keys(data[0]);

          setColumnDefs(
            keys.map((k) => ({
              headerName: k,
              field: k,
              sortable: true,
              filter: true,
              resizable: true,
            }))
          );

          setRowData(data);
        },
      });
    };

    fetchFile();
  }, [filePath]);

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
      cellStyle: centerCellStyle,
    }),
    []
  );

  const rowHeight = 40;
  const headerHeight = 45;
  const padding = 20;

  const dynamicHeight = Math.min(
    rowData.length * rowHeight + headerHeight + padding,
    maxHeight
  );

  return (
    <div className="ag-theme-alpine" style={{ height: dynamicHeight, width: "100%" }}>
      {rowData.length > 0 ? (
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
        />
      ) : (
        <div style={{ textAlign: "center", padding: "1rem", color: "#666" }}>
          No data available for this file.
        </div>
      )}
    </div>
  );
};

export default ExcelViewer;
